import * as React from 'react'
import { usePathname } from '@repo/nextjs-utils/navigation'
import { useCurrentCurrency } from '@repo/cf-user/hooks/use-current-currency'
import type { Currency, CurrencyCode, Language } from 'codegen/types'
import { currencyCodeToCurrency } from 'cf-menu/utils/currencies'
import { isLoggedInSelector } from 'auth/selectors'
import { useUserQuery } from 'auth/hooks'
import {
  useUpdateUserCurrencyMutation,
  useUpdateUserLanguageMutation,
} from 'codegen/generated/user-preferences'
import { CURRENCY_COOKIE_NAME } from 'constants/cookies'
import { useCookies } from 'react-cookie'
import { useLangCookie } from 'cf-i18n/cookies'
import invariant from 'tiny-invariant'
import { stripDomain } from 'utils/url'

import { getCookieDomain } from 'utils/env'

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
invariant(cookieDomain, 'Cookie domain is not defined inside cf-header')

export const useLanguageAndCurrency = () => {
  const pathname = usePathname()
  const { data: userData } = useUserQuery()
  const isAuthenticated = isLoggedInSelector(userData?.me)

  const currentCurrency = useCurrentCurrency()
  const [activeCurrency, setActiveCurrency] = React.useState<Currency>(currentCurrency)
  const [, setCookie] = useCookies([CURRENCY_COOKIE_NAME])
  const [, setLangCookie] = useLangCookie()

  // eslint-disable-next-line cf-web/codegen-mutation-patterns
  const updateUserLanguage = useUpdateUserLanguageMutation()
  // eslint-disable-next-line cf-web/codegen-mutation-patterns
  const updateUserCurrency = useUpdateUserCurrencyMutation()

  const onChangeCurrency = React.useCallback(
    async (currencyCode: CurrencyCode) => {
      const currency = currencyCodeToCurrency(currencyCode)
      if (isAuthenticated) {
        await updateUserCurrency.mutateAsync({ currency: currencyCode })
      }

      setCookie(CURRENCY_COOKIE_NAME, currencyCode.toUpperCase(), {
        path: '/',
        domain: getCookieDomain(cookieDomain),
      })

      setActiveCurrency(currency)

      window.location.reload()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setActiveCurrency, isAuthenticated],
  )

  const onChangeLanguage = React.useCallback(
    async (language: Language) => {
      if (isAuthenticated) {
        await updateUserLanguage.mutateAsync({ language })
      }
      setLangCookie(language)
      const alternatePath =
        document.querySelector<HTMLLinkElement>(`link[hreflang="${language}"]`)?.href ?? ''
      const searchParams = window.location.search
      const newUrl = stripDomain(new URL(alternatePath))

      window.location.href = `${newUrl}${searchParams || ''}`
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, isAuthenticated, setLangCookie],
  )

  return {
    activeCurrency,
    onChangeCurrency,
    onChangeLanguage,
  }
}
